import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sparkles, Clapperboard, Crown, LogOut, Menu, X } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const authed = user && user !== false;
  const [open, setOpen] = useState(false);

  const links = [
    ["/studio", "Studio"],
    ["/gallery", "Gallery"],
    ["/pricing", "Pricing"],
    ...(authed && user.role === "admin" ? [["/admin", "Admin"]] : []),
  ];

  const link = (to, label) => (
    <Link
      key={to}
      to={to}
      data-testid={`nav-${label.toLowerCase()}`}
      className={`text-sm font-medium transition-colors hover:text-primary ${
        loc.pathname === to ? "text-primary" : "text-muted-foreground"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2" onClick={() => setOpen(false)}>
          <div className="w-9 h-9 rounded-xl bg-primary/15 grid place-items-center glow-cyan">
            <Clapperboard className="w-5 h-5 text-primary" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight">MotionCraft</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map(([to, label]) => link(to, label))}
        </nav>

        <div className="flex items-center gap-3">
          {authed && user.subscription_status === "active" && (
            <span data-testid="pro-badge" className="hidden sm:flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-accent/15 text-accent">
              <Crown className="w-3.5 h-3.5" /> Creator
            </span>
          )}
          {authed ? (
            <>
              <Button data-testid="nav-studio-cta" onClick={() => nav("/studio")} className="rounded-full font-semibold hidden sm:inline-flex" size="sm">
                <Sparkles className="w-4 h-4 mr-1" /> Create
              </Button>
              <Button data-testid="logout-btn" onClick={logout} variant="ghost" size="icon" className="rounded-full hidden md:inline-flex">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login" onClick={() => nav("/auth")} variant="ghost" size="sm" className="rounded-full hidden sm:inline-flex">Log in</Button>
              <Button data-testid="nav-signup" onClick={() => nav("/auth?mode=register")} size="sm" className="rounded-full font-semibold hidden sm:inline-flex">Get started</Button>
            </>
          )}

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="md:hidden w-10 h-10 rounded-xl grid place-items-center text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-border px-5 py-4 flex flex-col gap-4 glass">
          {links.map(([to, label]) => (
            <Link
              key={to}
              to={to}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className={`text-base font-semibold ${loc.pathname === to ? "text-primary" : "text-foreground"}`}
            >
              {label}
            </Link>
          ))}
          <div className="pt-2 border-t border-border flex flex-col gap-3">
            {authed ? (
              <>
                <Button data-testid="mobile-create" onClick={() => { setOpen(false); nav("/studio"); }} className="rounded-full font-semibold w-full">
                  <Sparkles className="w-4 h-4 mr-1" /> Create
                </Button>
                <Button data-testid="mobile-logout" onClick={() => { setOpen(false); logout(); }} variant="outline" className="rounded-full font-semibold w-full border-border">
                  <LogOut className="w-4 h-4 mr-1" /> Log out
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="mobile-login" onClick={() => { setOpen(false); nav("/auth"); }} variant="outline" className="rounded-full font-semibold w-full border-border">Log in</Button>
                <Button data-testid="mobile-signup" onClick={() => { setOpen(false); nav("/auth?mode=register"); }} className="rounded-full font-semibold w-full">Get started</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
